import { type FC, useEffect, useState } from "react";
import { LiveKitRoom, RoomAudioRenderer } from "@livekit/components-react";
import { VoiceSession } from "../../../core/types";
import ChatFormVoiceControls from "./ChatFormVoiceControls";
import SwitchingToVoiceModeMessage from "./SwitchingToVoiceModeMessage";
import { useCore } from "../../../contexts/useCore";

interface ChatFormVoiceProps {
  onExitVoiceMode: () => void;
}

const ChatFormVoice: FC<ChatFormVoiceProps> = ({ onExitVoiceMode }) => {
  const core = useCore();
  const [voiceSession, setVoiceSession] = useState<VoiceSession | null>(null);

  useEffect(() => {
    let cancelled = false;

    const initializeVoiceSession = async (): Promise<void> => {
      try {
        const session = await core.startVoice();
        if (cancelled) return;
        setVoiceSession(session);
      } catch {
        if (cancelled) return;
        onExitVoiceMode();
      }
    };

    void initializeVoiceSession();

    return () => {
      cancelled = true;
      void core.endVoice();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!voiceSession) {
    return (
      <SwitchingToVoiceModeMessage />
    );
  }

  return (
    <LiveKitRoom
      serverUrl={voiceSession.url}
      token={voiceSession.token}
      connect={true}
      audio={true}
      video={false}
      options={{
        audioCaptureDefaults: {
          autoGainControl: true,
          echoCancellation: true,
          noiseSuppression: true
        },
        publishDefaults: {
          stopMicTrackOnMute: true,
        }
      }}
    >
      <RoomAudioRenderer />
      <ChatFormVoiceControls onExitVoiceMode={onExitVoiceMode} />
    </LiveKitRoom>
  );
};

export default ChatFormVoice;
